import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { Check, Zap, ShieldCheck, AlertCircle, Users, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { euro } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const FEATURES = [
  "Pronostici illimitati su Serie A e Champions League",
  "Sistema 1-X-2 con +3 punti per esito corretto",
  "Classifica mensile e storica aggiornata in tempo reale",
  "Partecipazione ai premi ogni 30 giorni",
  "Dashboard personale con statistiche complete",
  "Annulla il rinnovo quando vuoi",
];

export default function Subscription() {
  const { user, settings, hasActiveSub, loadSubscription } = useApp();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showSponsor, setShowSponsor] = useState(false);
  const [sponsor, setSponsor] = useState("");
  const price = settings?.subscription_price || 4.99;
  const paypalOn = settings?.integrations?.paypal;

  useEffect(() => {
    if (params.get("subscription") === "cancel") toast.info("Procedura di abbonamento annullata.");
  }, [params]);

  const openCheckout = () => {
    if (!user) { nav("/registrati"); return; }
    if (user.referred_by) { startPayment(); return; }
    setShowSponsor(true);
  };

  const startPayment = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/create", {});
      if (data.approve_url) window.location.href = data.approve_url;
    } catch (e) { toast.error(apiErr(e)); setLoading(false); setShowSponsor(false); }
  };

  const confirmSponsor = async () => {
    if (sponsor.trim()) {
      try {
        const { data } = await api.post("/referral/set", { sponsor: sponsor.trim() });
        toast.success(`Sponsor "${data.sponsor_nickname}" associato!`);
      } catch (e) { toast.error(apiErr(e)); return; }
    }
    setShowSponsor(false);
    startPayment();
  };

  return (
    <div>
      <PageHeader overline="Abbonamento mensile" title="Abbonati alla Challenge" subtitle="Un unico piano, tutte le funzioni. Disdici quando vuoi." />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {hasActiveSub ? (
          <div className="surface-elevated p-8 text-center">
            <ShieldCheck className="mx-auto text-status-correct mb-3" size={48} />
            <h2 className="font-heading text-2xl font-bold text-white">Abbonamento Attivo</h2>
            <p className="text-muted-foreground mt-2">Hai già un abbonamento attivo. Puoi inviare i tuoi pronostici!</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/calendario" className="btn-gold">Vai al Calendario</Link>
              <Link to="/profilo" className="btn-outline-gold">Gestisci abbonamento</Link>
            </div>
          </div>
        ) : (
          <div className="surface overflow-hidden border-gold-300/40 shadow-gold">
            <div className="bg-gradient-to-br from-gold-300 to-gold-500 text-ink-900 p-8 text-center">
              <div className="font-heading uppercase tracking-widest text-sm font-bold">King of Resell Challenge</div>
              <div className="font-heading text-6xl font-black mt-2" data-testid="subscription-price">{euro(price)}</div>
              <div className="font-semibold">al mese · rinnovo automatico</div>
            </div>
            <div className="p-8">
              <ul className="space-y-3">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-200">
                    <Check size={20} className="text-gold-300 shrink-0 mt-0.5" /> <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              {!paypalOn && (
                <div className="flex items-start gap-2 bg-status-locked/10 border border-status-locked/30 rounded-lg p-3 mt-6 text-sm text-status-locked">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <span>Il pagamento con PayPal è in fase di configurazione. Riprova a breve o contatta l'assistenza.</span>
                </div>
              )}
              <button data-testid="subscribe-paypal-btn" disabled={loading || !paypalOn} onClick={openCheckout} className="btn-gold w-full !py-4 mt-6 text-base">
                <Zap size={20} /> {loading ? "Reindirizzamento…" : "Abbonati con PayPal"}
              </button>
              <div className="flex items-start gap-2 bg-gold-300/10 border border-gold-300/25 rounded-lg p-3 mt-4 text-sm text-gold-100">
                <Users size={18} className="shrink-0 mt-0.5 text-gold-300" />
                <span><b className="text-gold-300">Porta 3 amici, abbonamento gratis!</b> Quando 3 persone si abbonano con il tuo nickname come sponsor, non paghi più finché li mantieni attivi.</span>
              </div>
              {!user && <p className="text-xs text-center text-muted-foreground mt-3">Dovrai prima creare un account.</p>}
              <p className="text-xs text-center text-muted-foreground mt-4">Pagamento sicuro. Puoi annullare il rinnovo in qualsiasi momento dal tuo profilo.</p>
            </div>
          </div>
        )}
      </div>

      {showSponsor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/80 backdrop-blur-sm p-4" data-testid="sponsor-modal">
          <div className="surface-elevated w-full max-w-md p-6 relative animate-fade-up">
            <button onClick={() => setShowSponsor(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-white" data-testid="sponsor-close"><X size={20} /></button>
            <div className="grid place-items-center h-12 w-12 rounded-full bg-gold-300/10 text-gold-300 mb-4"><Users size={24} /></div>
            <h3 className="font-heading text-2xl font-bold uppercase text-white">Hai uno sponsor?</h3>
            <p className="text-sm text-muted-foreground mt-2">Inserisci il <b className="text-slate-200">nickname o l'ID</b> di chi ti ha invitato. Quando lo sponsor raggiunge 3 iscritti, il suo abbonamento diventa gratis. Campo facoltativo.</p>
            <label className="block mt-4">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">ID Sponsor</span>
              <input data-testid="sponsor-input" className="input-dark mt-1" placeholder="Nickname o ID account" value={sponsor} onChange={(e) => setSponsor(e.target.value)} />
            </label>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button data-testid="sponsor-confirm" disabled={loading} onClick={confirmSponsor} className="btn-gold flex-1">{loading ? "Attendi…" : "Continua con l'abbonamento"}</button>
              <button data-testid="sponsor-skip" disabled={loading} onClick={() => { setSponsor(""); setShowSponsor(false); startPayment(); }} className="btn-outline-gold">Salta</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
